import React from 'react';
import {
  Home,
  FileText,
  Plane,
  CreditCard,
  Activity,
  MessageCircle,
  Calendar,
  Users,
  LayoutTemplate,
  Clock,
  Truck,
  TrendingUp,
  BarChart,
  Bell,
  Building,
  Layers,
  Receipt,
  ShieldCheck,
  Shield,
  Settings,
  User,
} from 'lucide-react';
import { msg } from '@lingui/core/macro';
import type { UserRole } from '@shared/types/auth';
import type { MessageDescriptor } from '@lingui/core';

export interface NavItem {
  key: string;
  label: string | MessageDescriptor;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
  badge?: number;
  description?: string | MessageDescriptor;
}

export interface NavigationRegistryItem extends Omit<NavItem, 'label' | 'description'> {
  label: MessageDescriptor;
  description?: MessageDescriptor;
}

export interface NavigationRegistry {
  [role: string]: NavigationRegistryItem[];
}

export const NAV_REGISTRY: NavigationRegistry = {
  patient: [
    {
      key: 'dashboard',
      label: msg({ id: 'shared.lib.navigation.patient.dashboard', message: 'Dashboard' }),
      path: '/dashboard',
      icon: Home,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.dashboard-description', message: 'Your dashboard overview' }),
    },
    {
      key: 'quotes',
      label: msg({ id: 'shared.lib.navigation.patient.quotes', message: 'Quotes' }),
      path: '/quotes',
      icon: FileText,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.quotes-description', message: 'View your treatment quotes' }),
    },
    {
      key: 'travel',
      label: msg({ id: 'shared.lib.navigation.patient.travel', message: 'Travel' }),
      path: '/travel',
      icon: Plane,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.travel-description', message: 'Travel arrangements and itinerary' }),
    },
    {
      key: 'payments',
      label: msg({ id: 'shared.lib.navigation.patient.payments', message: 'Payments' }),
      path: '/payments',
      icon: CreditCard,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.payments-description', message: 'Payment history and methods' }),
    },
    {
      key: 'treatment',
      label: msg({ id: 'shared.lib.navigation.patient.treatment', message: 'Treatment' }),
      path: '/treatment',
      icon: Activity,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.treatment-description', message: 'Dental records and tooth chart' }),
    },
    {
      key: 'messages',
      label: msg({ id: 'shared.lib.navigation.patient.messages', message: 'Messages' }),
      path: '/messages',
      icon: MessageCircle,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.messages-description', message: 'Message your care team' }),
    },
    {
      key: 'settings',
      label: msg({ id: 'shared.lib.navigation.patient.settings', message: 'Settings' }),
      path: '/settings',
      icon: Settings,
      roles: ['patient'],
      description: msg({ id: 'shared.lib.navigation.patient.settings-description', message: 'Manage your account preferences' }),
    },
  ],
  doctor: [
    {
      key: 'dashboard',
      label: msg({ id: 'shared.lib.navigation.doctor.my-day', message: 'My Day' }),
      path: '/dashboard',
      icon: Calendar,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.my-day-description', message: "Today's schedule and tasks" }),
    },
    {
      key: 'patients',
      label: msg({ id: 'shared.lib.navigation.doctor.patients', message: 'Patients' }),
      path: '/patients',
      icon: Users,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.patients-description', message: 'Manage your patients' }),
    },
    {
      key: 'quotes',
      label: msg({ id: 'shared.lib.navigation.doctor.quotes', message: 'Quotes' }),
      path: '/quotes',
      icon: FileText,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.quotes-description', message: 'Create and manage treatment quotes' }),
    },
    {
      key: 'calendar',
      label: msg({ id: 'shared.lib.navigation.doctor.calendar', message: 'Calendar' }),
      path: '/calendar',
      icon: Calendar,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.calendar-description', message: 'Appointment scheduling' }),
    },
    {
      key: 'templates',
      label: msg({ id: 'shared.lib.navigation.doctor.templates', message: 'Templates' }),
      path: '/templates',
      icon: LayoutTemplate,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.templates-description', message: 'Treatment plan templates' }),
    },
    {
      key: 'payments',
      label: msg({ id: 'shared.lib.navigation.doctor.payments', message: 'Payments' }),
      path: '/payments',
      icon: CreditCard,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.payments-description', message: 'Payment tracking' }),
    },
    {
      key: 'messages',
      label: msg({ id: 'shared.lib.navigation.doctor.messages', message: 'Messages' }),
      path: '/messages',
      icon: MessageCircle,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.messages-description', message: 'Communicate with patients' }),
    },
    {
      key: 'settings',
      label: msg({ id: 'shared.lib.navigation.doctor.settings', message: 'Settings' }),
      path: '/settings',
      icon: Settings,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.settings-description', message: 'Manage operational email preferences' }),
    },
    {
      key: 'doctor-profile',
      label: msg({ id: 'shared.lib.navigation.doctor.profile', message: 'Profile' }),
      path: '/doctor-profile',
      icon: User,
      roles: ['doctor'],
      description: msg({ id: 'shared.lib.navigation.doctor.profile-description', message: 'Manage your public profile and availability' }),
    },
  ],
  admin: [
    {
      key: 'dashboard',
      label: msg({ id: 'shared.lib.navigation.admin.dashboard', message: 'Dashboard' }),
      path: '/dashboard',
      icon: Calendar,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.dashboard-description', message: 'Daily operational overview' }),
    },
    {
      key: 'patients',
      label: msg({ id: 'shared.lib.navigation.admin.patients', message: 'Patients' }),
      path: '/patients',
      icon: Users,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.patients-description', message: 'Patient management' }),
    },
    {
      key: 'quoting-queue',
      label: msg({ id: 'shared.lib.navigation.admin.quoting-queue', message: 'Quoting Queue' }),
      path: '/quoting-queue',
      icon: Clock,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.quoting-queue-description', message: 'Pending quote requests' }),
    },
    {
      key: 'logistics',
      label: msg({ id: 'shared.lib.navigation.admin.logistics', message: 'Logistics' }),
      path: '/logistics',
      icon: Truck,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.logistics-description', message: 'Travel and accommodation coordination' }),
    },
    {
      key: 'calendar',
      label: msg({ id: 'shared.lib.navigation.admin.calendar', message: 'Calendar' }),
      path: '/calendar',
      icon: Calendar,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.calendar-description', message: 'Master calendar view' }),
    },
    {
      key: 'sales',
      label: msg({ id: 'shared.lib.navigation.admin.sales', message: 'Sales' }),
      path: '/sales',
      icon: TrendingUp,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.sales-description', message: 'Sales metrics and tracking' }),
    },
    {
      key: 'payments',
      label: msg({ id: 'shared.lib.navigation.admin.payments', message: 'Payments' }),
      path: '/payments',
      icon: CreditCard,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.payments-description', message: 'Payment processing and tracking' }),
    },
    {
      key: 'contracts',
      label: msg({ id: 'shared.lib.navigation.admin.contracts', message: 'Contracts' }),
      path: '/contracts',
      icon: FileText,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.contracts-description', message: 'Contract management and tracking' }),
    },
    {
      key: 'procedure-catalog',
      label: msg({ id: 'shared.lib.navigation.admin.procedure-catalog', message: 'Procedure Catalog' }),
      path: '/admin/procedure-catalog',
      icon: Layers,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.procedure-catalog-description', message: 'Manage the global specialty procedure library' }),
    },
    {
      key: 'messages',
      label: msg({ id: 'shared.lib.navigation.admin.messages', message: 'Messages' }),
      path: '/messages',
      icon: MessageCircle,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.messages-description', message: 'Communicate with patients and doctors' }),
    },
    {
      key: 'reports',
      label: msg({ id: 'shared.lib.navigation.admin.reports', message: 'Reports' }),
      path: '/reports',
      icon: BarChart,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.reports-description', message: 'Analytics and reporting' }),
    },
    {
      key: 'settings',
      label: msg({ id: 'shared.lib.navigation.admin.settings', message: 'Settings' }),
      path: '/settings',
      icon: Settings,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.settings-description', message: 'Manage operational email preferences' }),
    },
    {
      key: 'users',
      label: msg({ id: 'shared.lib.navigation.admin.user-management', message: 'User Management' }),
      path: '/admin/users',
      icon: Users,
      roles: ['admin'],
      description: msg({ id: 'shared.lib.navigation.admin.user-management-description', message: 'Manage patients, doctors, and owners' }),
    },
  ],
  super_admin: [
    {
      key: 'dashboard',
      label: msg({ id: 'shared.lib.navigation.super-admin.home-dashboard', message: 'Home Dashboard' }),
      path: '/dashboard',
      icon: Home,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.home-dashboard-description', message: 'Platform overview and key metrics' }),
    },
    {
      key: 'users',
      label: msg({ id: 'shared.lib.navigation.super-admin.user-management', message: 'User Management' }),
      path: '/super-admin/users',
      icon: Users,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.user-management-description', message: 'Manage all users across organizations' }),
    },
    {
      key: 'verifications',
      label: msg({ id: 'shared.lib.navigation.super-admin.verifications', message: 'Verifications' }),
      path: '/super-admin/verifications',
      icon: ShieldCheck,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.verifications-description', message: 'Review pending provider verifications' }),
    },
    {
      key: 'patients',
      label: msg({ id: 'shared.lib.navigation.super-admin.patients', message: 'Patients' }),
      path: '/patients',
      icon: Users,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.patients-description', message: 'Review patient intake requests and manage patient records' }),
    },
    {
      key: 'organizations',
      label: msg({ id: 'shared.lib.navigation.super-admin.organizations', message: 'Organizations' }),
      path: '/super-admin/organizations',
      icon: Building,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.organizations-description', message: 'Manage client organizations' }),
    },
    {
      key: 'plans',
      label: msg({ id: 'shared.lib.navigation.super-admin.organization-plans', message: 'Organization Plans' }),
      path: '/super-admin/plans',
      icon: Layers,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.organization-plans-description', message: 'Configure pricing and permissions' }),
    },
    {
      key: 'procedure-catalog',
      label: msg({ id: 'shared.lib.navigation.super-admin.procedure-catalog', message: 'Procedure Catalog' }),
      path: '/super-admin/procedure-catalog',
      icon: Layers,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.procedure-catalog-description', message: 'Manage the global specialty procedure library' }),
    },
    {
      key: 'quoting-queue',
      label: msg({ id: 'shared.lib.navigation.super-admin.quoting-queue', message: 'Quoting Queue' }),
      path: '/super-admin/kanban',
      icon: Clock,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.quoting-queue-description', message: 'Platform-wide quote management' }),
    },
    {
      key: 'logistics',
      label: msg({ id: 'shared.lib.navigation.super-admin.logistics', message: 'Logistics' }),
      path: '/super-admin/logistics',
      icon: Truck,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.logistics-description', message: 'Platform-wide logistics tracking' }),
    },
    {
      key: 'billing',
      label: msg({ id: 'shared.lib.navigation.super-admin.billing', message: 'Billing' }),
      path: '/super-admin/billing',
      icon: Receipt,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.billing-description', message: 'Financial oversight and billing' }),
    },
    {
      key: 'payments',
      label: msg({ id: 'shared.lib.navigation.super-admin.payments', message: 'Payments' }),
      path: '/super-admin/payments',
      icon: CreditCard,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.payments-description', message: 'Payment processing and management' }),
    },
    {
      key: 'audit',
      label: msg({ id: 'shared.lib.navigation.super-admin.audit-logs', message: 'Audit Logs' }),
      path: '/super-admin/audit',
      icon: Shield,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.audit-logs-description', message: 'System audit and security logs' }),
    },
    {
      key: 'notifications',
      label: msg({ id: 'shared.lib.navigation.super-admin.notifications', message: 'Notifications' }),
      path: '/super-admin/notifications',
      icon: Bell,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.notifications-description', message: 'Broadcast notifications and campaigns' }),
    },
    {
      key: 'reports',
      label: msg({ id: 'shared.lib.navigation.super-admin.reports-analytics', message: 'Reports & Analytics' }),
      path: '/super-admin/reports',
      icon: BarChart,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.reports-analytics-description', message: 'Data visualization and analytics' }),
    },
    {
      key: 'messages',
      label: msg({ id: 'shared.lib.navigation.super-admin.messaging', message: 'Messaging' }),
      path: '/messages',
      icon: MessageCircle,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.messaging-description', message: 'Multi-user communication system' }),
    },
    {
      key: 'settings',
      label: msg({ id: 'shared.lib.navigation.super-admin.dashboard-settings', message: 'Dashboard Settings' }),
      path: '/super-admin/settings',
      icon: Settings,
      roles: ['super_admin'],
      description: msg({ id: 'shared.lib.navigation.super-admin.dashboard-settings-description', message: 'Dashboard customization and preferences' }),
    },
  ],
};

/**
 * Translate a navigation item using Lingui's t function
 */
export const translateNavItem = (
  item: NavigationRegistryItem,
  t: (descriptor: MessageDescriptor) => string
): NavItem => {
  return {
    ...item,
    label: t(item.label),
    description: item.description ? t(item.description) : undefined,
  };
};

/**
 * Get navigation items for a specific user role (untranslated - use getTranslatedNavItemsForRole in components)
 */
export const getNavItemsForRole = (role?: UserRole): NavigationRegistryItem[] => {
  if (!role) return [];
  return NAV_REGISTRY[role] || [];
};

/**
 * Get translated navigation items for a specific user role
 * Use this in React components where you have access to the t function
 */
export const getTranslatedNavItemsForRole = (
  t: (descriptor: MessageDescriptor) => string,
  role?: UserRole
): NavItem[] => {
  if (!role) return [];
  const items = NAV_REGISTRY[role] || [];
  return items.map(item => translateNavItem(item, t));
};

/**
 * Get the home path for a specific role
 */
export const getRoleHomePath = (role: UserRole): string => {
  const navItems = getNavItemsForRole(role);
  return navItems.length > 0 ? navItems[0].path : '/';
};

/**
 * Check if a path is valid for a user role
 */
export const isPathValidForRole = (path: string, role?: UserRole): boolean => {
  if (!role) return false;
  const navItems = getNavItemsForRole(role);
  return navItems.some(item => item.path === path);
};

/**
 * Get nav item by path for a specific role (untranslated)
 */
export const getNavItemByPath = (path: string, role?: UserRole): NavigationRegistryItem | undefined => {
  if (!role) return undefined;
  const navItems = getNavItemsForRole(role);
  return navItems.find(item => item.path === path);
};

/**
 * Get translated nav item by path for a specific role
 */
export const getTranslatedNavItemByPath = (
  path: string,
  t: (descriptor: MessageDescriptor) => string,
  role?: UserRole
): NavItem | undefined => {
  if (!role) return undefined;
  const navItem = getNavItemByPath(path, role);
  if (!navItem) return undefined;
  return translateNavItem(navItem, t);
};
